import { Trash2 } from "lucide-react";
import { fmtTime, MEAL_GROUP_META, MEAL_GROUP_ORDER } from "./utils";

/**
 * Renders today's entries grouped by meal (breakfast/lunch/dinner/snack/other).
 * Each row shows the timestamp, the note, macro chips, and a delete button.
 *
 * Pure presentational — parent owns fetching + delete.
 */
export default function MealGroupedEntries({ entries, tz, onDelete }) {
  // Group entries by meal_group (fallback → "other")
  const grouped = MEAL_GROUP_ORDER.reduce((acc, key) => {
    const rows = entries.filter((e) => (e.meal_group || "other") === key);
    if (rows.length > 0) acc[key] = rows;
    return acc;
  }, {});

  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([groupKey, rows]) => {
        const meta = MEAL_GROUP_META[groupKey] || MEAL_GROUP_META.other;
        const totals = rows.reduce(
          (acc, e) => ({
            calories: acc.calories + Number(e.calories || 0),
            protein_g: acc.protein_g + Number(e.protein_g || 0),
          }),
          { calories: 0, protein_g: 0 }
        );
        return (
          <div
            key={groupKey}
            className="border border-slate-200 bg-slate-50/40"
            data-testid={`meal-group-block-${groupKey}`}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50/60">
              <div className="flex items-center gap-2">
                <span className="text-base" aria-hidden>{meta.emoji}</span>
                <span className="font-display font-bold text-sm text-slate-900 dark:text-white uppercase tracking-wide">
                  {meta.label}
                </span>
                <span className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
                  · {rows.length} item{rows.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="font-mono-num text-[11px] text-slate-600 tabular-nums">
                <b className="text-slate-900 dark:text-white">{Math.round(totals.calories)}</b> kcal · <b className="text-slate-900 dark:text-white">{Math.round(totals.protein_g)}</b>g P
              </div>
            </div>
            <div className="divide-y divide-zinc-900">
              {rows.map((e) => (
                <div
                  key={e.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-50/40 transition-colors group"
                  data-testid={`nutrition-entry-${e.id}`}
                >
                  <div className="font-mono-num text-[11px] text-slate-500 w-14 shrink-0">{fmtTime(e.logged_at, tz)}</div>
                  <div className="flex-1 min-w-0">
                    {e.note && <div className="text-sm text-slate-900 dark:text-white truncate">{e.note}</div>}
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 font-mono-num text-[11px] mt-0.5">
                      <span className="text-emerald-400 tabular-nums">{Math.round(Number(e.calories) || 0)}<span className="text-slate-400 ml-0.5">kcal</span></span>
                      {Number(e.protein_g) > 0 && <span className="text-rose-400 tabular-nums">{Math.round(Number(e.protein_g))}<span className="text-slate-400 ml-0.5">g P</span></span>}
                      {Number(e.carbs_g) > 0 && <span className="text-blue-400 tabular-nums">{Math.round(Number(e.carbs_g))}<span className="text-slate-400 ml-0.5">g C</span></span>}
                      {Number(e.fat_g) > 0 && <span className="text-orange-400 tabular-nums">{Math.round(Number(e.fat_g))}<span className="text-slate-400 ml-0.5">g F</span></span>}
                      {Number(e.sugar_g) > 0 && <span className="text-pink-400 tabular-nums">{Math.round(Number(e.sugar_g))}<span className="text-slate-400 ml-0.5">g Sug</span></span>}
                      {Number(e.fiber_g) > 0 && <span className="text-violet-400 tabular-nums">{Math.round(Number(e.fiber_g))}<span className="text-slate-400 ml-0.5">g Fib</span></span>}
                      {Number(e.sodium_mg) > 0 && <span className="text-slate-400 tabular-nums">{Math.round(Number(e.sodium_mg))}<span className="text-slate-400 ml-0.5">mg Na</span></span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDelete(e.id)}
                    className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-[#FF3B30] transition-all"
                    aria-label="Delete entry"
                    data-testid={`nutrition-delete-${e.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
